/** CrosswordChampionPanel — MC/SuperAdmin: leaderboard view, champion selection, reveal start/end. */
import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Trophy, RefreshCw, Sparkles, Square } from "lucide-react";
import { T } from "../lib/i18n";

function fmtSecs(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function CrosswordChampionPanel() {
  const { eventId } = useMotif();
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [presentation, setPresentation] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [{ data: b }, { data: p }] = await Promise.all([
        api.get("/mc/crossword/leaderboard"),
        api.get("/presentation"),
      ]);
      setBoard(b);
      setPresentation(p);
      setSelected((sel) => sel || b.rows[0]?.guest_id || null);
    } catch (e) { /* silent — no published crossword */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "presentation_update") setPresentation(msg.payload);
    if (msg.type === "crossword_completed") refresh();
  });

  const revealActive = presentation?.state === "CROSSWORD_CHAMPION_REVEAL";
  const rows = board?.rows || [];
  const chosen = rows.find((r) => r.guest_id === selected) || rows[0];
  const isOverride = chosen && rows[0] && chosen.guest_id !== rows[0].guest_id;

  const startReveal = async () => {
    setBusy(true);
    try {
      await api.post("/mc/crossword/reveal/start", {
        guest_id: chosen?.guest_id, reason: isOverride ? (reason || "manual override") : undefined,
      });
      toast.success(`🏆 ${chosen.guest_name} revealed on MC Show and projector`);
      refresh();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const endReveal = async () => {
    setBusy(true);
    try {
      await api.post("/mc/crossword/reveal/end");
      toast.success("Reveal ended — back to Live Feed");
      refresh();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card-elev p-4" data-testid="mc-crossword-panel">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-lg text-motif">{T("Crossword Champion Reveal")}</div>
        <button onClick={refresh} className="btn-ghost text-[11px] px-2 py-1 ml-auto flex items-center gap-1" data-testid="crossword-lb-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {!board && <div className="text-motif-muted text-sm italic">{T("No published crossword.")}</div>}

      {board && rows.length === 0 && (
        <div className="text-motif-muted text-sm italic" data-testid="crossword-lb-empty">{T("No scored submissions yet.")}</div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs min-w-[560px]">
            <thead className="text-[9px] tracking-luxe text-motif-muted">
              <tr className="border-b border-motif text-left">
                <th className="py-1.5 pl-1"></th><th>#</th><th>{T("GUEST")}</th><th>{T("TABLE")}</th>
                <th className="text-right">{T("SCORE")}</th><th className="text-right">{T("CLUES")}</th>
                <th className="text-right">%</th><th className="text-right">{T("TIME")}</th><th className="text-right pr-1">{T("STATUS")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.guest_id} className="border-b border-motif" data-testid={`crossword-lb-row-${r.rank}`}>
                  <td className="py-1.5 pl-1">
                    <input type="radio" name="champ" checked={selected === r.guest_id}
                      onChange={() => setSelected(r.guest_id)} data-testid={`crossword-lb-select-${r.rank}`} />
                  </td>
                  <td className="font-display text-primary-motif">{r.rank}</td>
                  <td className="text-motif">{r.guest_name}</td>
                  <td className="text-motif-muted">{r.table_name || "—"}</td>
                  <td className="text-right tabular-nums font-medium">{(r.final_score ?? 0).toLocaleString()}</td>
                  <td className="text-right tabular-nums">{r.correct_clue_count}/{r.total_clue_count}</td>
                  <td className="text-right tabular-nums">{r.total_clue_count ? Math.round(r.correct_clue_count / r.total_clue_count * 100) : 0}%</td>
                  <td className="text-right tabular-nums">{fmtSecs(r.elapsed_seconds)}</td>
                  <td className="text-right pr-1">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{
                      background: r.submission_type === "COMPLETE" ? "var(--motif-primary)" : "var(--motif-accent)",
                      color: r.submission_type === "COMPLETE" ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
                    }}>{r.submission_type === "COMPLETE" ? "Complete" : "Partial"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {chosen && (
        <div className="mt-3 p-3 rounded-xl bg-accent-motif" data-testid="crossword-reveal-preview">
          <div className="text-[9px] tracking-luxe text-motif-muted mb-1">{T("PUBLIC REVEAL PREVIEW")}</div>
          <div className="font-display text-motif">
            {chosen.guest_name.split(" ")[0]} {chosen.guest_name.split(" ").slice(-1)[0][0]}. · {(chosen.final_score ?? 0).toLocaleString()} pts · {chosen.correct_clue_count}/{chosen.total_clue_count} · {chosen.submission_type === "COMPLETE" ? "Complete" : "Partial"}
          </div>
          {isOverride && (
            <input
              className="input-motif text-xs mt-2 w-full"
              placeholder="Reason for manual override (audited)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="crossword-override-reason"
            />
          )}
        </div>
      )}

      <div className="flex gap-2 mt-3">
        {!revealActive && chosen && (
          <button onClick={startReveal} disabled={busy}
            className="btn-primary text-sm h-10 flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="crossword-reveal-start">
            <Sparkles className="w-4 h-4" /> REVEAL CROSSWORD CHAMPION
          </button>
        )}
        {revealActive && (
          <button onClick={endReveal} disabled={busy}
            className="btn-primary text-sm h-10 flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="crossword-reveal-end">
            <Square className="w-4 h-4" /> End Reveal · Return to Live Feed
          </button>
        )}
      </div>
      {revealActive && (
        <div className="text-[10px] text-motif-muted mt-2 italic" data-testid="crossword-reveal-live">
          Champion reveal is live on MC Show and the projector.
        </div>
      )}
    </div>
  );
}
